import Big from 'big.js'
import { OfferType } from 'src/common/apollo/types/swarmx/graphql'
import { DataItem } from 'src/common/components/MarketDepthChart/types'
import { Offer } from 'src/common/types/models/Offer'
import { ZERO } from 'src/common/utils/big-utils'

export const MD_DECIMALS = 8

export const getMarketDepthData = (
  offers: Pick<
    Offer,
    'availableAmount' | 'amountIn' | 'amountOut' | 'offerType'
  >[],
  marketPrice?: Big | null
): DataItem[] => {
  if (!offers.length) return []

  // Step1: Group offers by price and prepare data for chart
  const priceMap = new Map<string, { blockOffers: Big; partialOffers: Big }>()

  if (marketPrice) {
    priceMap.set(marketPrice.toFixed(MD_DECIMALS), {
      blockOffers: ZERO,
      partialOffers: ZERO,
    })
  }

  offers.forEach(offer => {
    if (!offer.amountOut) return
    const pricePerUnit = offer.amountOut
      .div(offer.amountIn)
      .toFixed(MD_DECIMALS)

    if (!priceMap.has(pricePerUnit)) {
      priceMap.set(pricePerUnit, { blockOffers: ZERO, partialOffers: ZERO })
    }

    const entry = priceMap.get(pricePerUnit)!

    if (offer.offerType === OfferType.BlockOffer) {
      entry.blockOffers = offer.availableAmount.plus(entry.blockOffers)
    } else {
      entry.partialOffers = offer.availableAmount.plus(entry.partialOffers)
    }
  })

  let preparedData = Array.from(priceMap.entries())
    .map(([price, { blockOffers, partialOffers }]) => ({
      blockOffers: blockOffers.toNumber(),
      partialOffers: partialOffers.toNumber(),
      price: Number(price),
    }))
    .sort((a, b) => (a.price > b.price ? 1 : -1))

  // Autofill up to N data points to make chart consistent and centered
  const N = (40 - preparedData.length) / 2
  for (let i = 0; i < N; i++) {
    preparedData.unshift({ blockOffers: 0, partialOffers: 0, price: 0 })
  }

  for (let i = 0; i < N; i++) {
    preparedData.push({ blockOffers: 0, partialOffers: 0, price: 0 })
  }

  // Step 2: Convert Map to sorted array
  return preparedData
}

export const formatDataKey = (key: string): string => {
  // Insert a space before each capital letter
  const withSpaces = key.replace(/([A-Z])/g, ' $1')
  // Capitalize the first character and trim any extra spaces
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).trim()
}

export const getTooltipRangeData = (data: DataItem[], index: number) => {
  const rangeData = data.slice(0, index + 1)

  const rangeAmount = rangeData.reduce(
    (sum, item) => sum + item.blockOffers + item.partialOffers,
    0
  )

  const weightedPriceSum = rangeData.reduce(
    (sum, item) => sum + item.price * (item.blockOffers + item.partialOffers),
    0
  )

  const rangePrice = rangeAmount > 0 ? weightedPriceSum / rangeAmount : 0

  return {
    rangeAmount,
    rangePrice,
  }
}
